import { faChevronDown, faChevronRight, faTimeline } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { serviceCodeDropdownOptions } from '@/components/PersonInsurancePolicy/NewVOB/serviceCodeOptions';
import type { ExternalPolicyVerification } from '@/types/externalVerification';
import { Pill, VendorLogo, vendorDisplayName } from '@/ui';
import { currencyInCentsFormatter } from '@/utils/currencies';

import './styles.css';
import type { Props } from './types';

export const serviceCodeLabel = (code: string | null | undefined, separator = ' · ') => {
  if (!code) return null;

  const option = serviceCodeDropdownOptions.find(({ value }) => value === code);
  if (!option) return code;

  return `${code}${separator}${option.label.slice(`${code} - `.length)}`;
};

const responseDate = (epv: ExternalPolicyVerification) =>
  epv.created_at ? new Date(epv.created_at).toLocaleDateString('en-US') : null;

const value = (content: React.ReactNode | null | undefined) =>
  content ?? <span className="text-uniform-gray-400">—</span>;

const cents = (amount: number | null | undefined) =>
  amount == null ? null : currencyInCentsFormatter(amount);

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-xs text-uniform-gray-500">{label}</span>
    <span className="text-sm font-medium">{value(children)}</span>
  </div>
);

const SummaryChips = ({ epv }: { epv: ExternalPolicyVerification }) => {
  const eligibility = epv.eligibility;
  const date = responseDate(epv);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-uniform-gray-500">Payer response</span>
      <Pill variant="outline" color="neutral" size="sm">
        <VendorLogo source={epv.source ?? epv.vendor} size="sm" showName />
        {date && ` · ${date}`}
      </Pill>
      {eligibility?.eligibility_status_description && (
        <Pill variant="outline" color="neutral" size="sm">
          {eligibility.eligibility_status_description}
        </Pill>
      )}
      {eligibility?.coordination_of_benefits_order && (
        <Pill variant="outline" color="neutral" size="sm">
          {`COB: ${eligibility.coordination_of_benefits_order}`}
        </Pill>
      )}
      {epv.service_code && (
        <Pill variant="outline" color="neutral" size="sm">
          {serviceCodeLabel(epv.service_code)}
        </Pill>
      )}
      {eligibility?.is_medicare_part_a && (
        <Pill variant="outline" color="neutral" size="sm">
          Medicare A · Active
        </Pill>
      )}
      {eligibility?.is_medicare_part_b && (
        <Pill variant="outline" color="neutral" size="sm">
          Medicare B · Active
        </Pill>
      )}
      {eligibility?.is_hospice_care && (
        <Pill color="warning" size="sm">
          Hospice election on file
        </Pill>
      )}
      {epv.rejections?.map((rejection) => (
        <Pill key={rejection.id} color="destructive" size="sm">
          {rejection.description ?? 'Rejected'}
        </Pill>
      ))}
    </div>
  );
};

const FullResponse = ({
  epv,
  isLinkedToCurrentVerification,
  timelineUrl
}: {
  epv: ExternalPolicyVerification;
  isLinkedToCurrentVerification: boolean;
  timelineUrl?: string | null;
}) => {
  const [isRawOpen, setIsRawOpen] = useState(false);
  const eligibility = epv.eligibility;
  const finance = epv.finance;
  const date = responseDate(epv);

  const copays = [
    ['Office', finance?.office_copay_in_cents],
    ['Specialist', finance?.specialist_copay_in_cents],
    ['Hospital', finance?.hospital_copay_in_cents],
    ['Other', finance?.other_copay_in_cents]
  ] as const;

  return (
    <section aria-label="Full payer response" className="flex flex-col gap-4 text-sm">
      <div className="flex flex-wrap items-center gap-2.5">
        <VendorLogo source={epv.source ?? epv.vendor} size="lg" />
        <strong>{`${vendorDisplayName(epv.source ?? epv.vendor)} response`}</strong>
        <span className="text-uniform-gray-500">
          {[date, epv.service_code && `service code ${epv.service_code}`]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {isLinkedToCurrentVerification && (
          <Pill color="secondary" size="sm">
            Linked to the current verification
          </Pill>
        )}
        <div className="grow" />
        {timelineUrl && (
          <Link
            to={timelineUrl}
            className="inline-flex items-center gap-1.5 font-medium text-blue-700">
            <FontAwesomeIcon icon={faTimeline} className="h-3.5 w-3.5" />
            Open in Timeline
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
        <Fact label="Eligibility">
          {eligibility?.eligibility_status_description &&
            `${eligibility.eligibility_status_description}${eligibility.eligibility_status_code ? ` (${eligibility.eligibility_status_code})` : ''}`}
        </Fact>
        <Fact label="Coordination of benefits">{eligibility?.coordination_of_benefits_order}</Fact>
        <Fact label="Medicare A / B">
          {eligibility?.is_medicare_part_a || eligibility?.is_medicare_part_b
            ? `${eligibility.is_medicare_part_a ? 'A' : ''}${eligibility.is_medicare_part_a && eligibility.is_medicare_part_b ? ' · ' : ''}${eligibility.is_medicare_part_b ? 'B' : ''} active`
            : null}
        </Fact>
        <Fact label="Hospice">
          {eligibility ? (eligibility.is_hospice_care ? 'Yes' : 'No') : null}
        </Fact>
        <Fact label="Plan">{epv.external_name}</Fact>
        <Fact label="Group">{epv.group_number ?? epv.group_name}</Fact>
        <Fact label="Effective">{epv.effective_on}</Fact>
        <Fact label="Terminates">{epv.terminate_on}</Fact>
      </div>

      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-uniform-gray-300 text-uniform-gray-500">
            <th className="py-2 font-medium">Finance</th>
            <th className="w-48 py-2 font-medium">In-network</th>
            <th className="w-48 py-2 font-medium">Out-of-network</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-uniform-gray-100">
            <td className="py-2">Deductible</td>
            <td className="py-2 font-medium">{value(cents(finance?.deductible_in_cents))}</td>
            <td className="py-2 font-medium">
              {value(cents(finance?.out_of_network_deductible_in_cents))}
            </td>
          </tr>
          <tr className="border-b border-uniform-gray-100">
            <td className="py-2">Deductible remaining</td>
            <td className="py-2 font-medium">
              {value(cents(finance?.deductible_remaining_in_cents))}
            </td>
            <td className="py-2 font-medium">
              {value(cents(finance?.out_of_network_deductible_remaining_in_cents))}
            </td>
          </tr>
          <tr className="border-b border-uniform-gray-100">
            <td className="py-2">Out-of-pocket max</td>
            <td className="py-2 font-medium">
              {value(cents(finance?.out_of_pocket_max_in_cents))}
            </td>
            <td className="py-2 font-medium">
              {value(cents(finance?.out_of_network_out_of_pocket_max_in_cents))}
            </td>
          </tr>
          <tr className="border-b border-uniform-gray-100">
            <td className="py-2">Out-of-pocket remaining</td>
            <td className="py-2 font-medium">
              {value(cents(finance?.out_of_pocket_remaining_in_cents))}
            </td>
            <td className="py-2 font-medium">
              {value(cents(finance?.out_of_network_out_of_pocket_remaining_in_cents))}
            </td>
          </tr>
          <tr className="border-b border-uniform-gray-100">
            <td className="py-2">Coinsurance</td>
            <td className="py-2 font-medium">
              {value(
                finance?.coinsurance_percentage != null
                  ? `${finance.coinsurance_percentage}%`
                  : null
              )}
            </td>
            <td className="py-2 font-medium">
              {value(
                finance?.out_of_network_coinsurance_percentage != null
                  ? `${finance.out_of_network_coinsurance_percentage}%`
                  : null
              )}
            </td>
          </tr>
          <tr>
            <td className="py-2">Copays</td>
            <td className="py-2 font-medium" colSpan={2}>
              {copays.map(([label, amount], index) => (
                <span key={label}>
                  {index > 0 && ' · '}
                  {label} {amount != null ? currencyInCentsFormatter(amount) : '—'}
                </span>
              ))}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <strong>{`Coverages (${epv.coverages?.length ?? 0})`}</strong>
          {epv.coverages?.length ? (
            epv.coverages.map((coverage, index) => (
              <div
                key={index}
                className="flex items-center justify-between border-b border-uniform-gray-100 py-1.5">
                <span>
                  {[coverage.services?.join(', '), coverage.plan_description ?? coverage.plan_name]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <span className="font-medium text-green-800">
                  {coverage.eligibility_status_description}
                </span>
              </div>
            ))
          ) : (
            <span className="py-1.5 text-uniform-gray-500">None</span>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <strong>Rejections</strong>
          {epv.rejections?.length ? (
            epv.rejections.map((rejection) => (
              <div key={rejection.id} className="py-1.5 text-red-800">
                {[rejection.source, rejection.description].filter(Boolean).join(' · ')}
              </div>
            ))
          ) : (
            <span className="py-1.5 text-uniform-gray-500">None</span>
          )}
        </div>
      </div>

      {epv.raw_data && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            aria-expanded={isRawOpen}
            onClick={() => setIsRawOpen((open) => !open)}
            className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-uniform-gray-700">
            <FontAwesomeIcon
              icon={isRawOpen ? faChevronDown : faChevronRight}
              className="h-3 w-3"
            />
            Raw response
          </button>
          {isRawOpen && (
            <pre className="max-h-96 overflow-auto rounded-md border border-uniform-gray-200 bg-uniform-gray-50 p-3 text-xs">
              {JSON.stringify(epv.raw_data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </section>
  );
};

const PayerResponse = ({
  verification,
  view = 'summary',
  fullEpv,
  isLoading = false,
  timelineUrl,
  className
}: Props) => {
  const summaryEpv = verification?.external_policy_verification;
  if (!summaryEpv) return null;

  if (view === 'summary') {
    return (
      <div className={className}>
        <SummaryChips epv={summaryEpv} />
      </div>
    );
  }

  if (isLoading && !fullEpv) {
    return (
      <div aria-busy="true" className={`flex flex-col gap-3 ${className ?? ''}`}>
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 rounded bg-uniform-gray-200" />
          <div className="h-3 w-56 rounded-full bg-uniform-gray-200" />
        </div>
        <div className="h-3 w-3/4 rounded-full bg-uniform-gray-200" />
        <div className="h-3 w-1/2 rounded-full bg-uniform-gray-200" />
      </div>
    );
  }

  const epv = fullEpv ?? summaryEpv;

  return (
    <div className={className}>
      <FullResponse
        epv={epv}
        isLinkedToCurrentVerification={epv.id === summaryEpv.id}
        timelineUrl={timelineUrl}
      />
    </div>
  );
};

export default React.memo(PayerResponse);
